import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTemplates, generateTemplate, uploadDocument } from '../services/api';
import toast from 'react-hot-toast';

const JURISDICTIONS = ['HK', 'UK', 'US', 'SG', 'AU', 'CN'];

export default function TemplatesPage() {
  const { currentUser } = useAuth();
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [generatedText, setGeneratedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await getTemplates(token);
      setTemplates(res.templates || []);
    } catch { /* ok */ }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const selectTemplate = (t: any) => {
    setSelectedTemplate(t);
    setGeneratedText('');
    // Initialize params with empty values
    const initial: Record<string, string> = {};
    t.fields.forEach((f: string) => { initial[f] = f === 'jurisdiction' ? 'HK' : ''; });
    setParams(initial);
  };

  const handleGenerate = async () => {
    if (!currentUser || !selectedTemplate) return;
    setLoading(true); setGeneratedText('');
    try {
      const token = await currentUser.getIdToken();
      const res = await generateTemplate(token, {
        template_type: selectedTemplate.id,
        params,
      });
      setGeneratedText(res.document_text);
      toast.success('Document generated!');
    } catch (e: any) { toast.error(e.message || 'Generation failed'); }
    finally { setLoading(false); }
  };

  const handleDownload = () => {
    const blob = new Blob([generatedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTemplate?.name || 'document'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveToVault = async () => {
    if (!currentUser || !generatedText || !selectedTemplate) return;
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const blob = new Blob([generatedText], { type: 'text/plain' });
      const file = new File([blob], `${selectedTemplate.name}.txt`, { type: 'text/plain' });
      await uploadDocument(token, file, {
        label: `${selectedTemplate.name} — Generated`,
        doc_type: selectedTemplate.id === 'nda' ? 'nda' : selectedTemplate.id === 'employment' ? 'employment' : selectedTemplate.id === 'lease' ? 'lease' : 'contract',
        jurisdiction: params.jurisdiction || 'HK',
      });
      toast.success('Saved to Document Vault!');
    } catch (e: any) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const formatFieldName = (field: string) =>
    field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contract Templates</h1>
        <p className="text-gray-500 text-sm mt-1">Generate customized legal documents using AI. Review carefully before use.</p>
      </div>

      {/* Template gallery */}
      {!selectedTemplate && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {templates.map((t) => (
            <button key={t.id} onClick={() => selectTemplate(t)}
              className="text-left bg-white rounded-2xl border border-gray-200 p-5 hover:border-blue-300 hover:bg-blue-50/30 transition-all">
              <div className="text-3xl mb-2">{t.icon}</div>
              <p className="font-semibold text-gray-800 text-sm">{t.name}</p>
              <p className="text-xs text-gray-500 mt-1">{t.description}</p>
            </button>
          ))}
        </div>
      )}

      {/* Selected template form */}
      {selectedTemplate && !generatedText && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{selectedTemplate.icon}</span>
              <div>
                <h2 className="font-semibold text-gray-800">{selectedTemplate.name}</h2>
                <p className="text-xs text-gray-500">{selectedTemplate.description}</p>
              </div>
            </div>
            <button onClick={() => { setSelectedTemplate(null); setGeneratedText(''); }}
              className="text-sm text-gray-400 hover:text-gray-600">← Back to templates</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {selectedTemplate.fields.map((field: string) => (
              <div key={field}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{formatFieldName(field)}</label>
                {field === 'jurisdiction' ? (
                  <select value={params[field] || 'HK'}
                    onChange={(e) => setParams((p) => ({ ...p, [field]: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                ) : field === 'nda_type' ? (
                  <select value={params[field] || ''}
                    onChange={(e) => setParams((p) => ({ ...p, [field]: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select type</option>
                    <option value="mutual">Mutual NDA</option>
                    <option value="one-way">One-Way NDA</option>
                  </select>
                ) : field === 'ip_ownership' ? (
                  <select value={params[field] || ''}
                    onChange={(e) => setParams((p) => ({ ...p, [field]: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select ownership</option>
                    <option value="client">Client owns all IP</option>
                    <option value="contractor">Contractor retains IP, client gets license</option>
                    <option value="shared">Shared IP ownership</option>
                  </select>
                ) : (
                  <input type={field.includes('date') ? 'date' : 'text'}
                    value={params[field] || ''}
                    onChange={(e) => setParams((p) => ({ ...p, [field]: e.target.value }))}
                    placeholder={`Enter ${formatFieldName(field).toLowerCase()}`}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                )}
              </div>
            ))}
          </div>

          <button onClick={handleGenerate} disabled={loading}
            className="px-6 py-2.5 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors">
            {loading ? 'Generating...' : '✨ Generate Document'}
          </button>
        </div>
      )}

      {/* Generated document preview */}
      {generatedText && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Generated: {selectedTemplate?.name}</h2>
              <div className="flex gap-2">
                <button onClick={handleDownload}
                  className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors">
                  📥 Download TXT
                </button>
                <button onClick={handleSaveToVault} disabled={saving}
                  className="px-4 py-2 bg-blue-700 text-white rounded-lg text-xs font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors">
                  {saving ? 'Saving...' : '📁 Save to Vault'}
                </button>
                <button onClick={() => { setGeneratedText(''); }}
                  className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors">
                  ← Edit Parameters
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-[600px] overflow-y-auto font-mono border border-gray-200">
              {generatedText}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
            <span className="font-medium">Important: </span>
            This document was generated by AI and should be reviewed by a qualified lawyer before use. It is a starting point, not final legal advice.
          </div>
        </div>
      )}
    </div>
  );
}
